import React, { ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ChallengeBadges_memberBadgesByCategory } from "../../../types/__generated__/ChallengeBadges";
import { BADGE_CATEGORY_IDS, I18N_NAMESPACES } from "../../../helpers/constants";
import { colors } from "../../../styles/variables";
import CustomTabBar from "../../atoms/CustomTabBar";
import { BadgeRelatedWines_badgeRelatedWines as BadgeRelatedWines } from "../../../types/__generated__/BadgeRelatedWines";
import { BadgeWinesDescription, BadgeRelatedWinesStats, BadgeRelatedWinesStatsWrapper } from "./style";

interface BadgeRelatedWinesHeaderProps {
  shouldConcealWines: boolean;
  isRecommendedSelected: boolean;
  badge: ChallengeBadges_memberBadgesByCategory | null | undefined;
  setIsRecommendedSelected: Function;
  badgeRelatedWines: BadgeRelatedWines | null | undefined;
  contentTypeModelName: string;
  isBadgeEarned?: boolean;
  isBadgeWinesLoading?: boolean;
}

const BadgeRelatedWinesHeader = ({
  shouldConcealWines,
  isRecommendedSelected,
  badge,
  setIsRecommendedSelected,
  badgeRelatedWines,
  contentTypeModelName,
  isBadgeEarned = false,
  isBadgeWinesLoading = false,
}: BadgeRelatedWinesHeaderProps) => {
  const isChallengeBadge = badge?.badgeCategory?.id === BADGE_CATEGORY_IDS.DB_ID_BADGE_CATEGORY_CHALLENGES;

  const { t } = useTranslation([I18N_NAMESPACES.MY_BADGES, I18N_NAMESPACES.COMMON]);

  const progressType = isChallengeBadge
    ? t("myBadges:badgeRecommendations.progressTypes.challenge")
    : t("myBadges:badgeRecommendations.progressTypes.badge");

  const tableDescription = isRecommendedSelected
    ? t("myBadges:badgeRecommendations.recommended.description", {
        progressType,
      })
    : t("myBadges:badgeRecommendations.alreadyDrunk.description", {
        progressType,
      });

  if (shouldConcealWines) {
    return (
      <BadgeWinesDescription addTopMargin={false}>
        {t("myBadges:challengeRecommendations.startChallenge")}
      </BadgeWinesDescription>
    );
  }

  const renderBadgeRelatedWinesStatsSection = (): ReactElement => {
    const numberOfWinesTried = badgeRelatedWines?.numberOfWinesTried ?? 0;
    const averageRating = badgeRelatedWines?.averageRating ?? 0;

    if (!numberOfWinesTried) {
      return (
        <BadgeRelatedWinesStats>
          {t("myBadges:badgeRecommendations.zeroTried", {
            contentTypeModel: contentTypeModelName,
          })}
        </BadgeRelatedWinesStats>
      );
    }

    if (numberOfWinesTried && averageRating === 0) {
      return (
        <BadgeRelatedWinesStats>
          <Trans
            i18nKey="myBadges:badgeRecommendations.zeroAverageRating"
            components={{ bold: <strong /> }}
            count={numberOfWinesTried}
            values={{ contentTypeModel: contentTypeModelName }}
          />
        </BadgeRelatedWinesStats>
      );
    }

    return (
      <BadgeRelatedWinesStats>
        <Trans
          i18nKey="myBadges:badgeRecommendations.stats"
          components={{ bold: <strong /> }}
          count={numberOfWinesTried}
          values={{ averageRating, contentTypeModel: contentTypeModelName }}
        />
      </BadgeRelatedWinesStats>
    );
  };

  return (
    <>
      {!isBadgeEarned && (
        <>
          <CustomTabBar
            firstTabLabel={t("myBadges:badgeRecommendations.recommended.title")}
            secondTabLabel={t("myBadges:badgeRecommendations.alreadyDrunk.title")}
            isFirstTabSelected={isRecommendedSelected}
            onFirstTabClick={() => setIsRecommendedSelected(true)}
            onSecondTabClick={() => setIsRecommendedSelected(false)}
            bgColor={colors.blue}
            borderColor={colors.white}
          />
          <BadgeWinesDescription addTopMargin>{tableDescription}</BadgeWinesDescription>
        </>
      )}
      <BadgeRelatedWinesStatsWrapper>
        {!isBadgeWinesLoading && renderBadgeRelatedWinesStatsSection()}
      </BadgeRelatedWinesStatsWrapper>
    </>
  );
};

export default BadgeRelatedWinesHeader;
